import {
  signUp,
  confirmSignUp,
  signIn,
  signOut,
  getCurrentUser,
  fetchUserAttributes,
  updateUserAttributes,
  updatePassword,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
  fetchAuthSession,
  AuthUser,
} from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { User } from '../types'
import { logger } from './logger'
import axios from 'axios'

// Authentication Error Types
export class AuthenticationError extends Error {
  constructor(
    message: string,
    public code?: string
  ) {
    super(message)
    this.name = 'AuthenticationError'
  }
}

// User mapping utility
const mapCognitoUserToUser = async (cognitoUser: AuthUser): Promise<User> => {
  try {
    const attributes = await fetchUserAttributes()
    let role: 'Admin' | 'User' = 'User'
    if (attributes['custom:roles']) {
      try {
        // Try to parse as JSON set (e.g., '{"Admin"}')
        let roles = attributes['custom:roles']
        // Cognito sometimes returns sets as JSON strings or as comma-separated
        if (typeof roles === 'string') {
          // Try JSON.parse, fallback to string manipulation
          try {
            const parsed = JSON.parse(roles)
            if (Array.isArray(parsed)) {
              // Case-insensitive check: convert all to lowercase
              const hasAdmin = parsed.some((r) => String(r).toLowerCase() === 'admin')
              if (hasAdmin) {
                role = 'Admin'
              }
            } else if (typeof parsed === 'string') {
              // Case-insensitive check: convert to lowercase
              if (parsed.toLowerCase() === 'admin') {
                role = 'Admin'
              }
            }
          } catch {
            // Fallback: remove braces and quotes, split by comma
            const cleaned = roles.replace(/[{}"']/g, '')
            const roleArr = cleaned.split(',').map((r) => r.trim())
            // Case-insensitive check: convert all to lowercase
            const hasAdmin = roleArr.some((r) => r.toLowerCase() === 'admin')
            if (hasAdmin) {
              role = 'Admin'
            }
          }
        }
      } catch {
        // Case-insensitive check: convert to lowercase
        const rolesValue = String(attributes['custom:roles']).toLowerCase()
        if (rolesValue === 'admin') {
          role = 'Admin'
        }
      }
    }
    return {
      id: cognitoUser.userId,
      name:
        attributes.name ||
        attributes.preferred_username ||
        attributes.email?.split('@')[0] ||
        'User',
      email: attributes.email || '',
      role,
      status: cognitoUser.signInDetails?.loginId ? 'Active' : 'Pending',
      profilePicture: attributes.picture,
      createdAt: new Date().toISOString(), // Cognito doesn't provide creation date in user attributes
      updatedAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
    }
  } catch (error) {
    logger.error('Error mapping Cognito user to User type', error as Error)

    // Fallback user object
    return {
      id: cognitoUser.userId,
      name: cognitoUser.signInDetails?.loginId?.split('@')[0] || 'User',
      email: cognitoUser.signInDetails?.loginId || '',
      role: 'User',
      status: 'Active',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      lastLoginAt: new Date().toISOString(),
    }
  }
}

// Authentication Service Class
export class AuthService {
  private static instance: AuthService
  private listeners: Array<(user: User | null) => void> = []

  private constructor() {
    // Listen for authentication events
    Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          logger.info('User signed in', {
            userId: 'data' in payload ? payload.data?.userId : undefined,
          })
          this.handleAuthStateChange()
          break
        case 'signedOut':
          logger.info('User signed out')
          this.notifyListeners(null)
          break
        case 'tokenRefresh':
          logger.debug('Token refreshed')
          break
        default:
          // Handle other events or sign in failures
          if (payload.event.includes('failure')) {
            logger.warn('Authentication event failed', {
              event: payload.event,
              data: 'data' in payload ? payload.data : undefined,
            })
          }
          break
      }
    })
  }

  public static getInstance(): AuthService {
    if (!AuthService.instance) {
      AuthService.instance = new AuthService()
    }
    return AuthService.instance
  }

  // Add authentication state listener
  public addListener(callback: (user: User | null) => void): () => void {
    this.listeners.push(callback)
    return () => {
      this.listeners = this.listeners.filter((listener) => listener !== callback)
    }
  }

  private notifyListeners(user: User | null) {
    this.listeners.forEach((listener) => listener(user))
  }

  private async handleAuthStateChange() {
    try {
      const cognitoUser = await getCurrentUser()
      const user = await mapCognitoUserToUser(cognitoUser)
      this.notifyListeners(user)
    } catch (error) {
      this.notifyListeners(null)
    }
  }

  // Get current authenticated user
  public async getCurrentUser(): Promise<User | null> {
    try {
      const cognitoUser = await getCurrentUser()
      return await mapCognitoUserToUser(cognitoUser)
    } catch (error) {
      logger.debug('No authenticated user found')
      return null
    }
  }

  // Sign in user
  public async signIn(email: string, password: string): Promise<User> {
    try {
      logger.info('Sign in attempt', { email })

      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password,
      })

      if (!isSignedIn) {
        throw new AuthenticationError(
          `Sign in not completed. Next step: ${nextStep.signInStep}`,
          nextStep.signInStep
        )
      }

      const cognitoUser = await getCurrentUser()
      const user = await mapCognitoUserToUser(cognitoUser)

      logger.info('Sign in successful', { userId: user.id, email: user.email })
      return user
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error)
      logger.error('Sign in failed', error, { email })
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Sign up new user
  public async signUp(
    email: string,
    password: string,
    name: string
  ): Promise<{ user: User | null; nextStep: string }> {
    try {
      logger.info('Sign up attempt', { email, name })

      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
            'custom:role': 'User',
          },
        },
      })

      if (isSignUpComplete && userId) {
        const cognitoUser = await getCurrentUser()
        const user = await mapCognitoUserToUser(cognitoUser)
        logger.info('Sign up completed', { userId: user.id })
        return { user, nextStep: 'DONE' }
      }

      logger.info('Sign up initiated, confirmation required', { email })
      return { user: null, nextStep: nextStep.signUpStep }
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error)
      logger.error('Sign up failed', error, { email })
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Confirm sign up with verification code
  public async confirmSignUp(
    email: string,
    confirmationCode: string,
    password: string
  ): Promise<void> {
    try {
      logger.info('Confirming sign up', { email })
      await axios.post(
        'https://6gtnh7zplh.execute-api.ap-southeast-1.amazonaws.com/prod/post-api',
        {
          event_type: 'confirm-signup',
          email,
          confirmation_code: confirmationCode,
          password,
        }
      )
      logger.info('Sign up confirmed successfully', { email })
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error)
      logger.error('Sign up confirmation failed', error, { email })
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Sign out user
  public async signOut(): Promise<void> {
    try {
      await signOut()
      logger.info('Sign out successful')
    } catch (error: any) {
      logger.error('Sign out failed', error)
      throw new AuthenticationError('Sign out failed', error.name)
    }
  }

  // Reset password
  public async resetPassword(email: string): Promise<void> {
    try {
      logger.info('Password reset requested', { email })

      await resetPassword({ username: email })

      logger.info('Password reset code sent', { email })
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error)
      logger.error('Password reset failed', error, { email })
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Confirm password reset
  public async confirmPasswordReset(
    email: string,
    confirmationCode: string,
    newPassword: string
  ): Promise<void> {
    try {
      logger.info('Confirming password reset', { email })

      await confirmResetPassword({
        username: email,
        confirmationCode,
        newPassword,
      })

      logger.info('Password reset confirmed', { email })
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error)
      logger.error('Password reset confirmation failed', error, { email })
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Change password (for authenticated users)
  public async changePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      logger.info('Changing password for authenticated user')

      await updatePassword({ oldPassword, newPassword })

      logger.info('Password changed successfully')
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error, 'changePassword')
      logger.error('Password change failed', error)
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Update user profile
  public async updateProfile(updates: Partial<User>): Promise<User> {
    try {
      logger.info('Updating user profile', { updates })

      const attributeUpdates: Record<string, string> = {}

      if (updates.name) attributeUpdates.name = updates.name
      if (updates.profilePicture) attributeUpdates.picture = updates.profilePicture

      await updateUserAttributes({
        userAttributes: attributeUpdates,
      })

      const cognitoUser = await getCurrentUser()
      const user = await mapCognitoUserToUser(cognitoUser)

      logger.info('Profile updated successfully', { userId: user.id })
      return user
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error)
      logger.error('Profile update failed', error)
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Resend confirmation code
  public async resendConfirmationCode(email: string): Promise<void> {
    try {
      logger.info('Resending confirmation code', { email })

      await resendSignUpCode({ username: email })

      logger.info('Confirmation code resent', { email })
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error)
      logger.error('Resend confirmation code failed', error, { email })
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Get user-friendly error messages
  private getErrorMessage(error: any, context?: string): string {
    switch (error.name) {
      case 'UserNotFoundException':
        return 'No account found with this email address'
      case 'NotAuthorizedException':
        if (context === 'changePassword') {
          return 'Current password was incorrect'
        }
        return 'Incorrect email or password'
      case 'InvalidPasswordException':
        return 'Password does not meet requirements'
      case 'UsernameExistsException':
        return 'An account with this email already exists'
      case 'InvalidParameterException':
        return 'Invalid email format'
      case 'CodeMismatchException':
        return 'Invalid verification code'
      case 'ExpiredCodeException':
        return 'Verification code has expired'
      case 'LimitExceededException':
        return 'Too many attempts. Please try again later'
      case 'TooManyRequestsException':
        return 'Too many requests. Please wait before trying again'
      case 'NetworkError':
        return 'Network error. Please check your connection'
      default:
        return error.message || 'An unexpected error occurred'
    }
  }

  // Get current access token with debugging
  public async getCurrentAccessToken(): Promise<string> {
    try {
      const session = await fetchAuthSession()

      // Debug logging
      logger.debug('Auth session fetched', {
        hasTokens: !!session.tokens,
        hasAccessToken: !!session.tokens?.accessToken,
        hasIdToken: !!session.tokens?.idToken,
      })

      const accessToken = session.tokens?.accessToken?.toString()
      if (!accessToken) {
        logger.error('No access token available in session')
        throw new AuthenticationError('No access token available')
      }

      // Log token format for debugging (first and last 10 characters only for security)
      const tokenPreview = `${accessToken.substring(0, 10)}...${accessToken.substring(accessToken.length - 10)}`
      logger.debug('Access token retrieved', {
        tokenLength: accessToken.length,
        tokenPreview,
        tokenParts: accessToken.split('.').length, // JWT should have 3 parts
      })

      // Validate JWT format (should have 3 parts separated by dots)
      const tokenParts = accessToken.split('.')
      if (tokenParts.length !== 3) {
        logger.warn('Invalid JWT format - token does not have 3 parts', {
          parts: tokenParts.length,
          tokenPreview,
        })
      }

      return accessToken
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error, 'getCurrentAccessToken')
      logger.error('Failed to get current access token', error)
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Get current JWT token for API calls (uses ID token which contains user profile info)
  public async getCurrentJWTToken(): Promise<string> {
    try {
      const session = await fetchAuthSession()

      // Debug logging
      logger.debug('Auth session fetched for JWT token', {
        hasTokens: !!session.tokens,
        hasAccessToken: !!session.tokens?.accessToken,
        hasIdToken: !!session.tokens?.idToken,
      })

      const idToken = session.tokens?.idToken?.toString()
      if (!idToken) {
        logger.error('No ID token available in session')
        throw new AuthenticationError('No ID token available')
      }

      // Log token format for debugging (first and last 10 characters only for security)
      const tokenPreview = `${idToken.substring(0, 10)}...${idToken.substring(idToken.length - 10)}`
      logger.debug('ID token retrieved for API calls', {
        tokenLength: idToken.length,
        tokenPreview,
        tokenParts: idToken.split('.').length, // JWT should have 3 parts
      })

      // Validate JWT format (should have 3 parts separated by dots)
      const tokenParts = idToken.split('.')
      if (tokenParts.length !== 3) {
        logger.warn('Invalid JWT format - ID token does not have 3 parts', {
          parts: tokenParts.length,
          tokenPreview,
        })
      }

      return idToken
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error, 'getCurrentJWTToken')
      logger.error('Failed to get current JWT token', error)
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Get ID token (alternative for some backends)
  public async getCurrentIdToken(): Promise<string> {
    try {
      const session = await fetchAuthSession()
      const idToken = session.tokens?.idToken?.toString()
      if (!idToken) {
        throw new AuthenticationError('No ID token available')
      }

      logger.debug('ID token retrieved', {
        tokenLength: idToken.length,
        tokenParts: idToken.split('.').length,
      })

      return idToken
    } catch (error: any) {
      const errorMessage = this.getErrorMessage(error, 'getCurrentIdToken')
      logger.error('Failed to get current ID token', error)
      throw new AuthenticationError(errorMessage, error.name)
    }
  }

  // Debug method to get token payload without signature verification
  public async debugTokenPayload(): Promise<any> {
    try {
      const accessToken = await this.getCurrentAccessToken()
      const tokenParts = accessToken.split('.')

      if (tokenParts.length !== 3) {
        throw new Error('Invalid JWT format')
      }

      // Decode payload (base64url decode)
      const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')))

      logger.debug('Token payload decoded', {
        issuer: payload.iss,
        audience: payload.aud,
        subject: payload.sub,
        expiration: new Date(payload.exp * 1000).toISOString(),
        issuedAt: new Date(payload.iat * 1000).toISOString(),
        tokenUse: payload.token_use,
        clientId: payload.client_id,
      })

      return payload
    } catch (error) {
      logger.error('Failed to decode token payload', error as Error)
      throw error
    }
  }
}

// Export singleton instance
export const authService = AuthService.getInstance()
